import { useCallback, useEffect, useState } from 'react';
import {
  API,
  getFromLocalStorageByKey,
  trackLink,
  LoggerInstance,
  getClickToCallWithKeyOrUrl,
} from '@vfit/shared/data-access';
import {
  EMethodCode,
  IAvailablePaymentMethods,
  IOptionalText,
  ISIAFlowType,
  IUpdateProductPriceType,
  IWarningInfo,
} from '@vfit/shared/models';
import {
  CONSUMER_CMS_SHOPPING_CART,
  getCurrentUserType,
  GetCustomerResponse,
  IBillingAccountResponse,
  IFindCallerResponse,
  IGenericErrorCMS,
  IGlobalConfigCMS,
  IPayments,
  IS_AUTOMATIC_RECHARGE_SELECTED,
  IS_SELECTED_WALLET,
  IUserType,
  productSpecificError,
  SELECTED_PAYMENT,
  useCmsConfig,
} from '@vfit/consumer/data-access';
import { Skeleton, WarningInfo } from '@vfit/shared/components';
import { CheckboxCards } from '@vfit/consumer/components';
import { useCheckout } from '../../../../../iBuyMobile.context';
import {
  organizeCheckBoxPayment,
  organizeCheckBoxPaymentItems,
  organizePaymentMethodError,
  SKELETON_SHAPE,
} from '../../recurrentPaymentCard.utils';
import { ID_FLOWS } from '../../../../checkout.constants';
import { CONTEXT_KEY } from '../../../../../iBuyMobile.context.data';
import { ICheckoutData } from '../../../../checkout.models';
import { updateProductPrice } from '../../../../../ShoppingCart/shoppingCart.utils';
import { useUpdatePaymentMethodFlow } from '../../../../hooks/UseUpdatePaymentMethodFlow/useUpdatePaymentMethodFlow';
import { useQueryClient } from 'react-query';
import TaggingDefaultWalletRecurrentPaymentCard from './defaultRecurrentPaymentCard.tagging';

const DefaultRecurrentPaymentCard = ({
  handleOnChangeEnableGoNext,
  handleGoPrevSlide,
}: ICheckoutData) => {
  const {
    currentStepKey,
    checkoutErrors,
    availablePaymentMethods,
    hideStickyBar,
    customerFlow,
    isModalButtonSticky,
    product,
    setProduct,
    setCurrentStepKey,
    setAvailablePaymentMethods,
    setIsLastCard,
    setUpdateProductPriceCtrl,
    setHideStickyBar,
    setUpfrontPayment,
    setIsDisabledBackButton,
    setIsPreselectedCard,
    setIsLoadingGoNext,
  } = useCheckout();
  const queryClient = useQueryClient();
  const genericError = useCmsConfig(
    CONSUMER_CMS_SHOPPING_CART,
    API.CMS_GET_GENERIC_ERROR_MOBILE
  ) as IGenericErrorCMS;
  const {
    paymentsmobile: { warningPriceChange, warningWaiting },
  } = useCmsConfig(CONSUMER_CMS_SHOPPING_CART, API.CMS_GET_PAYMENTS_MOBILE) as IPayments;
  const cmsWarningUpdateOffer: IWarningInfo = {
    title: warningPriceChange?.title || '',
    subtitle: warningPriceChange?.subtitle || '',
    description: warningPriceChange?.description || '',
  };
  const cmsWarningWaitingOffer: IWarningInfo = {
    title: warningWaiting?.title || warningPriceChange?.title || '',
    subtitle: warningWaiting?.subtitle || warningPriceChange?.subtitle || '',
    description: warningWaiting?.description || warningPriceChange?.description || '',
    lottie: product?.loaders?.choosePayment || '',
  };
  const { buttonPaymentError, urlPaymentErrorCallMeNow } = productSpecificError(product);
  const isFamilyPlus = product?.isFamilyPlusProduct;
  const paymentKeyCMS = isFamilyPlus
    ? API.CMS_GET_FAMILY_PLUS_PAYMENT
    : API.CMS_GET_PAYMENTS_MOBILE;
  const paymentCMS = useCmsConfig(CONSUMER_CMS_SHOPPING_CART, paymentKeyCMS) as IPayments;
  const {
    messageSelectionPaymentError,
    titleSelectionPaymentError,
    buttonLabelSelectionPayment,
    iconSelectionPayment,
    titleSelectedPaymentError,
    messageSelectedPaymentError,
    buttonLabelSelectedPayment,
    iconSelectedPayment,
  } = organizePaymentMethodError(paymentCMS);
  const { title, description, modal } = organizeCheckBoxPayment(paymentCMS);
  const [items, setItems] = useState<Array<IOptionalText>>([]);
  const [enablePut, setEnablePut] = useState(false);
  const [isShowUpdatePrice, setIsShowUpdatePrice] = useState(false);
  const billingAccount = getFromLocalStorageByKey('billingAccount') as IBillingAccountResponse;
  const paymentData = customerFlow?.payment?.data;
  const paymentIsLoading = customerFlow?.payment?.isLoading;
  const paymentIsError = customerFlow?.payment?.isError;
  const paymentIsSuccess = customerFlow?.payment?.isSuccess;

  // region NEW MOP enabler
  const cmsGlobalConfig = useCmsConfig(
    CONSUMER_CMS_SHOPPING_CART,
    API.CMS_GET_GLOBAL_CONFIG
  ) as IGlobalConfigCMS;
  const customerData: GetCustomerResponse[] = getFromLocalStorageByKey('customerData');
  const findCaller: IFindCallerResponse = getFromLocalStorageByKey('findCaller');
  const customerDataCF =
    customerData?.[0]?.owningIndividual?.fiscalCode || findCaller?.individualRef?.fiscalCode;
  const isGlobalBsNewMopMobile =
    cmsGlobalConfig?.globalconfig?.newMop?.enableNewMopMobile === 'true';

  const isCustomerXBS = customerDataCF?.includes('XBS');
  const isNewMopEnabled = isCustomerXBS || isGlobalBsNewMopMobile;

  // end region

  const isTNPUser =
    (getCurrentUserType() === IUserType.NEXT_USER_ACTIVE &&
      billingAccount?.hasPendingDeviceWithInstallments) ||
    !isNewMopEnabled;

  const {
    isLoading: isLoadingUpdatePaymentMethod,
    isSuccess: isSuccessUpdatePaymentMethod,
    isError: isErrorUpdatePaymentMethod,
    error: errorUpdatePaymentMethod,
    handleResetState: resetPutPaymentState,
  } = useUpdatePaymentMethodFlow(
    paymentData?.orderPayment?.orderItemPayments?.[0]?.orderItemOnBillPayment
      ?.billingOfferIdForChangePaymentMethod || [''],
    paymentData?.orderPayment?.orderItemPayments?.[0]?.orderActionId || '',
    enablePut,
    availablePaymentMethods?.find((availablePayment) => availablePayment.configured),
    ISIAFlowType.RECURRENT,
    undefined,
    setIsLoadingGoNext
  );

  useEffect(() => {
    // reset for back SIA
    window.history.pushState(null, '', window.location.pathname);
    setCurrentStepKey(ID_FLOWS.RECURRENT_PAYMENT_CARD);
    setIsPreselectedCard(false);
    setUpfrontPayment(undefined);
    localStorage.removeItem(IS_AUTOMATIC_RECHARGE_SELECTED);
    setIsLastCard(true);
    return () => {
      setIsLastCard(false);
    };
  }, []);

  const handleSelect = useCallback(
    (item: IOptionalText | undefined) => {
      LoggerInstance.debug('HANDLE SELECT IN AUTHORIZE', item);
      if (!item) return;
      const availablePaymentMethodsPrev: Array<IAvailablePaymentMethods> =
        availablePaymentMethods.map((method) => ({
          ...method,
          configured: method.methodCode === item.value,
          selection: method.methodCode === item.value,
        }));
      setAvailablePaymentMethods(availablePaymentMethodsPrev);
      localStorage.setItem(SELECTED_PAYMENT, item.value || '');
      if (item.value !== EMethodCode.WALLET) {
        setEnablePut(true);
        setIsLastCard(true);
        setUpdateProductPriceCtrl({
          isUpdatePrice: false,
          isPriceUpdated: true,
        });
        handleOnChangeEnableGoNext?.(false);
      } else {
        setUpdateProductPriceCtrl({
          isUpdatePrice: true,
          isPriceUpdated: false,
        });
        setIsLastCard(false);
        setEnablePut(false);
        handleOnChangeEnableGoNext?.(true);
      }
    },
    [availablePaymentMethods]
  );

  useEffect(() => {
    const resetPaymentData = () => {
      const availablePaymentMethodsPrev: Array<IAvailablePaymentMethods> =
        paymentData?.orderPayment.orderOnBillPayment?.availablePaymentMethods?.map((method) => ({
          ...method,
          selection: false,
        })) || [];

      setAvailablePaymentMethods(availablePaymentMethodsPrev || []);
      setItems(
        organizeCheckBoxPaymentItems(
          paymentCMS,
          paymentData?.orderPayment.orderOnBillPayment?.availablePaymentMethods || [],
          -1,
          billingAccount?.availableNewPayMeans || [],
          isTNPUser
        )
      );
    };
    if (!paymentIsLoading && !paymentIsError && paymentData) {
      const availablePaymentMethodsPrev: Array<IAvailablePaymentMethods> =
        paymentData?.orderPayment.orderOnBillPayment?.availablePaymentMethods?.map(
          (method, index) => ({
            ...method,
            selection: availablePaymentMethods[index]?.selection,
          })
        ) || [];
      setAvailablePaymentMethods(availablePaymentMethodsPrev || []);
      setItems(
        organizeCheckBoxPaymentItems(
          paymentCMS,
          paymentData?.orderPayment.orderOnBillPayment?.availablePaymentMethods || [],
          // Fix 28/10 => Loader Go back SIA, force user selection
          // availablePaymentMethods?.findIndex?.((aPm) => aPm.selection === true)
          -1,
          billingAccount?.availableNewPayMeans || [],
          isTNPUser
        )
      );
    }
    if (!paymentIsLoading && paymentIsError) {
      const action = () => {
        if (handleGoPrevSlide) handleGoPrevSlide();
      };
      resetPaymentData();
      setHideStickyBar(false);

      checkoutErrors?.showAndTrackError?.(
        genericError,
        action,
        customerFlow?.paymean?.error || undefined,
        titleSelectedPaymentError,
        messageSelectedPaymentError,
        action,
        buttonLabelSelectedPayment || buttonPaymentError,
        () =>
          getClickToCallWithKeyOrUrl(
            queryClient,
            urlPaymentErrorCallMeNow,
            product,
            action,
            currentStepKey,
            customerFlow?.paymean?.error
          ),
        product?.removeCTC ? undefined : product?.genericErrorCallMeNow?.label || '',
        isModalButtonSticky,
        true,
        false,
        false,
        iconSelectedPayment || ''
      );
    }
  }, [paymentData, paymentIsError, paymentIsLoading]);

  const getSelectedItem = () => {
    const selectionOption = items?.find((i) => i.selection);
    return {
      title: selectionOption?.title || '',
      value: selectionOption?.value || '',
    };
  };

  useEffect(() => {
    const resetPaymentData = () => {
      const availablePaymentMethodsPrev: Array<IAvailablePaymentMethods> =
        paymentData?.orderPayment.orderOnBillPayment?.availablePaymentMethods?.map((method) => ({
          ...method,
          selection: false,
        })) || [];
      setAvailablePaymentMethods(availablePaymentMethodsPrev || []);
      setItems(
        organizeCheckBoxPaymentItems(
          paymentCMS,
          paymentData?.orderPayment.orderOnBillPayment?.availablePaymentMethods || [],
          -1,
          billingAccount?.availableNewPayMeans || [],
          isTNPUser
        )
      );
    };
    if (isLoadingUpdatePaymentMethod && localStorage.getItem(CONTEXT_KEY)) {
      localStorage.removeItem(CONTEXT_KEY);
    }
    if (
      isSuccessUpdatePaymentMethod &&
      !isLoadingUpdatePaymentMethod &&
      !isErrorUpdatePaymentMethod
    ) {
      if (isShowUpdatePrice) {
        setProduct({
          ...product,
          ...updateProductPrice(product, IUpdateProductPriceType.RESET),
        });
        setHideStickyBar(false);
        setIsShowUpdatePrice(false);
        localStorage.removeItem(IS_SELECTED_WALLET);
      }
      handleOnChangeEnableGoNext?.(true);
      setEnablePut(false);
      resetPutPaymentState();
    }
    if (isErrorUpdatePaymentMethod && !isLoadingUpdatePaymentMethod) {
      setEnablePut(false);
      const action = () => {
        if (handleGoPrevSlide) handleGoPrevSlide();
      };
      resetPaymentData();
      setHideStickyBar(false);

      checkoutErrors?.showAndTrackError?.(
        genericError,
        action,
        errorUpdatePaymentMethod || undefined,
        titleSelectionPaymentError,
        messageSelectionPaymentError,
        action,
        buttonLabelSelectionPayment || buttonPaymentError,
        () =>
          getClickToCallWithKeyOrUrl(
            queryClient,
            urlPaymentErrorCallMeNow,
            product,
            action,
            currentStepKey,
            errorUpdatePaymentMethod || undefined
          ),
        product?.removeCTC ? undefined : product?.genericErrorCallMeNow?.label || '',
        isModalButtonSticky,
        true,
        false,
        false,
        iconSelectionPayment || ''
      );
    }
  }, [isSuccessUpdatePaymentMethod, isLoadingUpdatePaymentMethod, isErrorUpdatePaymentMethod]);

  useEffect(() => {
    const goNextButton = document.getElementById('sticky-offer-next');
    const trackGoNext = () => {
      const selectedPaymentMethod = availablePaymentMethods?.find((el) => el.configured);
      let linkName = '';
      switch (selectedPaymentMethod?.methodCode) {
        case EMethodCode.BANK_ACCOUNT:
          linkName = 'iban';
          break;
        case EMethodCode.CREDIT_CARD:
          linkName = 'carta di credito';
          break;
        case EMethodCode.DEBIT_CARD:
          linkName = 'carta di debito';
          break;
        case EMethodCode.PREPAID_CARD:
          linkName = 'carta prepagata';
          break;
        case EMethodCode.PAYPAL:
          linkName = 'paypal';
          break;
        case EMethodCode.WALLET:
          linkName = 'credito residuo';
          break;
        default:
          break;
      }
      trackLink(linkName);
    };
    goNextButton?.addEventListener('click', trackGoNext);
    return () => {
      goNextButton?.removeEventListener('click', trackGoNext);
    };
  }, [availablePaymentMethods, hideStickyBar]);

  useEffect(() => {
    setIsDisabledBackButton(isShowUpdatePrice);
  }, [isShowUpdatePrice]);

  const getWarningConfig = () => {
    const retrieveAlreadySelectedWallet = localStorage.getItem(IS_SELECTED_WALLET);
    if (product?.isUpdatedPriceWallet || retrieveAlreadySelectedWallet) {
      return cmsWarningUpdateOffer;
    }
    return cmsWarningWaitingOffer;
  };

  if (paymentIsLoading && !isLoadingUpdatePaymentMethod && items?.length > 0) {
    return <Skeleton {...SKELETON_SHAPE} />;
  }

  return (
    <>
      <TaggingDefaultWalletRecurrentPaymentCard />
      {isShowUpdatePrice && <WarningInfo {...getWarningConfig()} />}
      <div style={{ display: isShowUpdatePrice ? 'none' : 'block' }}>
        <CheckboxCards
          items={items}
          title={title || ''}
          description={description}
          bottomModal={modal}
          selectedItem={getSelectedItem()}
          value={getSelectedItem().value}
          setSelectedItem={() => undefined}
          onSelectItem={handleSelect}
          isSuccess={paymentIsSuccess}
          disableItems={isLoadingUpdatePaymentMethod}
        />
      </div>
    </>
  );
};

export default DefaultRecurrentPaymentCard;

